import React, { useEffect, useState } from 'react';
import { dayjs } from 'shared/utils/date';
import { getDefaultDateRange, calculateDateRanges } from 'shared/constants/date';

import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

import { FormattedMessage } from 'react-intl';
import { Button, Spin, Tooltip } from 'antd';
import { PlusOutlined, InfoCircleOutlined, GlobalOutlined } from '@ant-design/icons';
import { EDIT_SEGMENT, VIEW_GLOBAL_SEGMENTS } from 'shared/constants/permissions';
import DateRangePicker from 'components/DateRangePicker';
import LicenseTag from 'components/LicenseTag';
import globalMessages from 'translations/messages';

import useDataSource from 'hooks/useDataSource';
import useUser from 'hooks/useUser';

import Segment, { MAX_ENABLED_SEGMENTS } from './Segment';
import BenchmarkSegment from './BenchmarkSegment';
import SegmentDrawer from './SegmentDrawer';
import messages from './messages';
import './filter.less';
import GlobalSegmentDrawer from './GlobalSegmentDrawer';

const SortableItem = SortableElement(({ children }) => (
  // Sortable Components should be wrapped by DOM element
  // in order to calculate its position
  <div className="filter__segment-list-item">
    {children}
  </div>
));

const SortableList = SortableContainer(({ children }) => (
  // Sortable Components should be wrapped by DOM element
  // in order to calculate its position
  <div className="filter__segment-list">
    {children}
  </div>
));

const Filter = ({
  customerId,
  filter,
  onChange,
  onRemoveSegment,
  onDuplicateSegment,
  enabledCount,
  hasBenchmarkContent,
  newSegment,
  setNewSegment,
}) => {
  const {
    surveyGroupId,
    displayedSurveyId,
    dateRange,
  } = filter || {};

  const { isSuperUser, hasCustomerPermission } = useUser();
  const { hasDataSourcePermission, getDataSourceLicenseSetting } = useDataSource();

  // Used to update the UI before server requests are completed when sorting
  const [filterSegments, setFilterSegments] = useState([]);
  const [benchmarkSegments, setBenchmarkSegments] = useState([]);
  const [segmentToEdit, setSegmentToEdit] = useState(false);
  const [mnanageGlobalSegments, setManageGlobalSegments] = useState(false);

  const [updatingDateRange, setUpdatingDateRange] = useState(false);

  useEffect(() => {
    if (filter?.filterSegments) {
      setFilterSegments(filter.filterSegments.filter(s => s.sortIndex !== -1));
    }
  }, [filter?.filterSegments]);

  useEffect(() => {
    if (filter?.benchmarkSegments) {
      setBenchmarkSegments(filter.benchmarkSegments.filter(s => s.isEnabled));
    }
  }, [filter?.benchmarkSegments]);

  const closeDrawers = () => {
    setSegmentToEdit(false);
    setNewSegment(false);
    setManageGlobalSegments(false);
  };

  const openSegmentForm = segmentId => setSegmentToEdit(segmentId);

  const handleAddNewSegment = async () => {
    setSegmentToEdit(true);
  };

  const handleGlobalSegments = async () => {
    setManageGlobalSegments(true);
  };

  const removeSegment = async (segmentId) => {
    await onRemoveSegment(segmentId);
    closeDrawers();
  };

  const duplicateSegment = async (segmentId) => {
    await onDuplicateSegment(segmentId);
    closeDrawers();
  };

  const handleDateChange = async (dataRange) => {
    setUpdatingDateRange(true);
    await onChange({ dateRange: getDefaultDateRange(dataRange) });
    setUpdatingDateRange(false);
  };

  const toggleSegment = (updateProp) => async (segmentId, isEnabled) => onChange({ [updateProp]: [{ id: segmentId, isEnabled }] });

  const updateSegment = async (segment) => {
    if (!segment.id) {
      segment.sortIndex = filterSegments.length;
    }
    await onChange({ filterSegments: [segment] });
    closeDrawers();
  };

  const handleSortEnd = (setStateMethod, current, updateProp) => async ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const sorted = arrayMoveImmutable(current, oldIndex, newIndex);
    setStateMethod(sorted);
    onChange({ [updateProp]: sorted.map(({ id }, sortIndex) => ({ id, sortIndex })) });
  };

  const getSwitchState = (segment) => {
    let switchDisabled = false;
    let switchTooltip;

    if (enabledCount === 1 && segment.isEnabled) {
      // The user can still enable other segments.
      switchDisabled = true;
      switchTooltip = <FormattedMessage {...messages.minEnabledSegments} />;
    }

    if (enabledCount >= MAX_ENABLED_SEGMENTS && !segment.isEnabled) {
      // The user can still disable a segment.
      switchDisabled = true;
      switchTooltip = <FormattedMessage {...messages.maxEnabledSegments} values={{ max: MAX_ENABLED_SEGMENTS }} />;
    }

    return {
      switchDisabled,
      switchTooltip,
    };
  };

  const dateRanges = calculateDateRanges(dayjs, dateRange);

  const staticSegment = filter?.filterSegments.find(s => s.sortIndex === -1);

  const userSegmentLimit = getDataSourceLicenseSetting('userSegmentLimit');
  const userSegmentCount = filterSegments.filter(s => !s.createdByEmail).length;
  const sharedSegmentCount = filterSegments.filter(s => s.shared).length;
  const matomoSegmentCount = filterSegments.filter(s => s.syncToMatomo).length;

  const segmentLimitReached = userSegmentLimit != null && userSegmentCount >= userSegmentLimit;
  const canCreateSegment = isSuperUser || (!segmentLimitReached && hasDataSourcePermission(EDIT_SEGMENT));
  const canUseGlobalSegments = hasCustomerPermission(VIEW_GLOBAL_SEGMENTS);

  const disabledBenchmarkSegments = filter?.benchmarkSegments.filter(s => !s.isEnabled);

  useEffect(() => {
    if (newSegment && canCreateSegment) {
      setSegmentToEdit(newSegment);
    }
  }, [newSegment]);

  return (
    <div className="filter">
      <Spin size="large" spinning={updatingDateRange} tip={<FormattedMessage {...globalMessages.loading} />}>
        {filter && (
          <>
            <div className="filter__control">
              <div className="filter__control-title">
                <FormattedMessage {...messages.dateRange} />
              </div>
              <DateRangePicker
                onChange={handleDateChange}
                value={dateRange}
                defaultValue={getDefaultDateRange()}
              />
            </div>

            {staticSegment && (
            <div className="filter__control">
              <Segment
                isStaticSegment
                segment={staticSegment}
                dateRanges={dateRanges}
                filterResponseCounts={staticSegment.responseCounts}
                onToggle={toggleSegment('filterSegments')}
                {...getSwitchState(staticSegment)}
              />
            </div>
            )}

            <div className="filter__control">
              <div className="filter__control-title">
                <FormattedMessage {...messages.segmentation} />
                {userSegmentLimit != null && (
                <span>
                  {userSegmentCount}
                  /
                  {userSegmentLimit}
                </span>
                )}
              </div>

              <SortableList
                onSortEnd={handleSortEnd(setFilterSegments, filterSegments, 'filterSegments')}
                lockAxis="y"
                helperClass="filter__sortable-helper"
              >
                {filterSegments.map((segment, sortIndex) => (
                  <SortableItem key={segment.id} index={sortIndex}>
                    <Segment
                      segment={segment}
                      dateRanges={dateRanges}
                      filterResponseCounts={staticSegment?.responseCounts}
                      onEdit={openSegmentForm}
                      onToggle={toggleSegment('filterSegments')}
                      {...getSwitchState(segment)}
                    />
                  </SortableItem>
                ))}
              </SortableList>
              <Button
                disabled={!canCreateSegment}
                type="primary"
                icon={<PlusOutlined />}
                block
                onClick={handleAddNewSegment}
              >
                <FormattedMessage {...messages.addSegment} />
              </Button>
              {canUseGlobalSegments && (
              <Button
                disabled={!canCreateSegment}
                type="primary"
                icon={<GlobalOutlined />}
                block
                onClick={handleGlobalSegments}
                style={{ marginTop: '1em' }}
              >
                Use global segment (Beta)
              </Button>
              )}
              {segmentLimitReached && <div style={{ margin: '.5em 0' }}><LicenseTag setting="userSegmentLimit" showTooltip popoverProps={{ placement: 'topRight' }} /></div>}
            </div>

            {hasBenchmarkContent && (benchmarkSegments.length > 0 || disabledBenchmarkSegments?.length > 0) && (
            <div className="filter__control">
              <div className="filter__control-title">
                <FormattedMessage {...messages.benchmarks} />
                <Tooltip title={<FormattedMessage {...messages.benchmarksHelp} />}>
                  <InfoCircleOutlined style={{ marginLeft: '.5em' }} />
                </Tooltip>
              </div>
              <SortableList
                onSortEnd={handleSortEnd(setBenchmarkSegments, benchmarkSegments, 'benchmarkSegments')}
                lockAxis="y"
                helperClass="filter__sortable-helper"
              >
                {benchmarkSegments.map((segment, index) => (
                  <SortableItem key={segment.id} index={index}>
                    <BenchmarkSegment
                      segment={segment}
                      onToggle={toggleSegment('benchmarkSegments')}
                    />
                  </SortableItem>
                ))}
              </SortableList>
              {disabledBenchmarkSegments.map((segment) => (
                <BenchmarkSegment
                  key={segment.id}
                  segment={segment}
                  onToggle={toggleSegment('benchmarkSegments')}
                />
              ))}
            </div>
            )}

            <SegmentDrawer
              customerId={customerId}
              sharedSegmentCount={sharedSegmentCount}
              matomoSegmentCount={matomoSegmentCount}
              surveyGroupId={surveyGroupId}
              displayedSurveyId={displayedSurveyId}
              dateRange={dateRange}
              filterSegment={filterSegments.find(s => s.id === segmentToEdit) || segmentToEdit}
              onSubmit={updateSegment}
              onRemove={removeSegment}
              onClose={closeDrawers}
              onDuplicate={duplicateSegment}
            />
            <GlobalSegmentDrawer
              open={mnanageGlobalSegments}
              filterSegments={filterSegments}
              customerId={customerId}
              surveyGroupId={surveyGroupId}
              displayedSurveyId={displayedSurveyId}
              dateRange={dateRange}
              onSubmit={updateSegment}
              onRemove={removeSegment}
              onClose={closeDrawers}
              onDuplicate={duplicateSegment}
            />
          </>
        )}
      </Spin>
    </div>
  );
};

export default Filter;
